// src/pages/Video/VideoList.tsx
import React, { useState, useEffect, useImperativeHandle } from "react";
import { useLoader } from "../../components/loader/loader";
import { useNavigate } from "react-router-dom";
import { getData, deleteData } from "../../services/Axios/Axios";
import { ApiUrl } from "../../config/apiConfig";
import { PaginationControls } from "../../models/pagination";
import NoRecord from "../../components/NoDataScreen/NoRecord";
import Pagination from "../../components/pagination/pagination";
import EditButton from "../../components/UIElements/EditButton";
import DeleteButton from "../../components/UIElements/DeleteButton";
import messageService from "../../services/message/messageService";
import { Pencil, Trash } from "lucide-react";

interface NewList {
  allData: any;
  setAllData: any;
  addEditModalOpen: any;
}

const VideoList = React.forwardRef((props: NewList, ref) => {
  const { show, hide } = useLoader();
  const [searchdata, setSearchQuery] = useState<any>("");
  const [pagination, setPagination] = useState(new PaginationControls());
  const [startIndex, setStartIndex] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    getDataALL(searchdata);
  }, [pagination.page, searchdata]);

  const getDataALL = async (searchdata?: any) => {
    const skip = (pagination.page - 1) * pagination.limit;
    const obj: any = { skip_number: skip };
    if (searchdata) obj.search = searchdata;

    try {
      const response = await getData(ApiUrl.listVideos, obj, { show, hide });

      const dataWithDuration = await Promise.all(
        response.data.map(async (video: any) => {
          if (!video.videoUrl) return video;

          const videoElement = document.createElement("video");
          videoElement.src = video.videoUrl;

          return new Promise((resolve) => {
            videoElement.onloadedmetadata = () => {
              const duration = formatDuration(videoElement.duration);
              resolve({ ...video, duration });
            };
            videoElement.onerror = () => resolve({ ...video, duration: "N/A" });
          });
        })
      );

      props.setAllData(dataWithDuration);
      setPagination((prev) => ({ ...prev, count: response.total_count }));
      setStartIndex(skip + 1);
    } catch (error) {
      console.error("Error fetching videos", error);
    }
  };

  useImperativeHandle(ref, () => ({
    getDataALL,
    handleSearchSubmit,
    handleSearchChange,
  }));

  const handlePageChange = ({ selected }: { selected: number }) => {
    setPagination((prev) => ({
      ...prev,
      page: selected + 1,
    }));
  };

  const handleSearchChange = (event: any) => {
    setSearchQuery(event);
  };

  const handleSearchSubmit = () => {
    getDataALL(searchdata);
  };

  const deleteVideo = async (video: any) => {
    const confirm = messageService();
    confirm.confirm("Delete this video", "").then(async (res) => {
      if (res.value) {
        try {
          await deleteData(ApiUrl.deleteVideos + video._id);
          getDataALL();
          confirm.toast("success", confirm.message.deleteSuccess);
        } catch (err) {
          confirm.toast("error", "Delete failed");
        }
      }
    });
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="p-4">
      {props.allData.length === 0 ? (
        <NoRecord noDataText="No videos found" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {props.allData.map((data: any, index: number) => (
            <div
              key={data._id}
              className="bg-white shadow-md rounded-lg overflow-hidden border border-gray-200 flex"
            >
              <div className="w-1/2">
                {data.videoUrl ? (
                  <video controls className="w-full h-full object-cover">
                    <source src={data.videoUrl} type="video/mp4" />
                  </video>
                ) : data.thumbnail ? (
                  <img
                    src={data.thumbnail}
                    alt="thumbnail"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    No Preview
                  </div>
                )}
              </div>

              <div className="w-1/2 p-4 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">{data.title}</h3>
                  <p className="text-sm text-gray-500 mb-2">
                    Duration: {data.duration || "N/A"}
                  </p>
                </div>

                <div className="flex gap-2 mt-4  w-full">

                  <button
                    className="text-blue-600 hover:text-blue-800 hover:scale-110 ease-in-out duration-500"
                    onClick={() => props.addEditModalOpen(data)}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800 hover:scale-110 ease-in-out duration-500"
                    onClick={() => deleteVideo(data)}
                    title="Delete"
                  >
                    <Trash className="w-4 h-4" />
                  </button>

                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {props.allData.length > 0 && (
        <div className="mt-6">
          <Pagination pagination={pagination} onPageChange={handlePageChange} />
        </div>
      )}
    </div>
  );
});

export default VideoList;
